import { Spin } from '@/ui/compat/antd';
import { useTranslation } from 'react-i18next';
import { WalletOutlined } from '@/ui/compat/icons';

const formatTnd = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '0,00';

const Row = ({ label, value, className = '' }) => (
  <div className={`flex justify-between items-center text-sm ${className}`}>
    <span className='text-gray-500'>{label}</span>
    <span className='font-semibold'>{formatTnd(value)} TND</span>
  </div>
);

const Card = ({ title, totalLabel, usedLabel, availableLabel, total, used, available }) => (
  <div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex-1 min-w-[260px]'>
    <div className='flex items-center gap-2 text-blue-700 mb-3'>
      <WalletOutlined />
      <h3 className='text-sm font-semibold'>{title}</h3>
    </div>
    <div className='space-y-2'>
      <Row label={totalLabel} value={total} />
      <Row
        label={usedLabel}
        value={used}
        className='text-orange-600 [&>span:first-child]:text-orange-500 [&>span:last-child]:text-orange-600'
      />
      <div className='border-t border-gray-100 pt-2'>
        <Row
          label={availableLabel}
          value={available}
          className={`font-bold ${
            Number(available) >= 0
              ? '[&>span:last-child]:text-green-700'
              : '[&>span:last-child]:text-red-600'
          }`}
        />
      </div>
    </div>
  </div>
);

/**
 * Presentational — receives `balance` from the dashboard, which fetches it
 * via AmicaleValidationApi.getBalance(). The backend computes validated amicale
 * reservation amounts directly from the reservations table, so the "Réservations
 * validées" line always reflects the actual deducted solde.
 */
const AmicaleBalanceCard = ({ balance, loading, error }) => {
  const { t } = useTranslation()
  if (loading) {
    return (
      <div className='bg-white rounded-2xl border border-gray-100 p-6 flex items-center justify-center'>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700'>
        {error}
      </div>
    );
  }

  return (
    <div className='flex flex-col md:flex-row gap-4'>
      <Card
        title={t('amicales.balance.debitCeiling')}
        totalLabel={t('amicales.balance.totalCeiling')}
        usedLabel={t('amicales.balance.validatedReservations')}
        availableLabel={t('amicales.balance.available')}
        total={balance?.plafond?.totalPlafond ?? 0}
        used={balance?.plafond?.usedAmount ?? 0}
        available={balance?.plafond?.availableAmount ?? 0}
      />
    </div>
  );
};

export default AmicaleBalanceCard;
